import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BsArrowDownSquareFill } from "react-icons/bs";
import BasicBreadcrumbs from "../../common/UI/BreadcrumbNavigation/BreadcrumbNavigation";
import Contact from "../../components/contact/Contact";
import { fetchServiceBySlug } from "../../api";
import { queryKeys } from "../../api/keys";

function ServicesDetails() {
  const { i18n } = useTranslation();
  const { slug } = useParams();
  const currentLang = i18n.language;

  const [showArrow, setShowArrow] = useState(true);

  const { data: service, isLoading, error } = useQuery({
    queryKey: queryKeys.service(slug, currentLang),
    queryFn: () => fetchServiceBySlug(slug, currentLang),
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setShowArrow(window.scrollY <= 300);
          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (isLoading) {
    return (
      <div className="mt-[-95px] min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-designColor"></div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="mt-[-95px] min-h-screen flex justify-center items-center">
        <p className="text-red-500 text-center">
          Error loading service details:{" "}
          {error?.message || "Service not found"}
        </p>
      </div>
    );
  }

  const mainImage =
    service.hero_image_url ||
    "https://via.placeholder.com/1920x1080?text=Service+Image";

  return (
    <div
      className="mt-[-95px] overflow-hidden"
      style={{ direction: currentLang === "ar" ? "rtl" : "ltr" }}
    >
      {/* HERO SECTION */}
      <div className="relative w-full min-h-screen overflow-hidden">
        {/* BACKGROUND IMAGE */}
        <div
          className="absolute inset-0 w-full h-full will-change-transform"
          style={{
            backgroundImage: `url(${mainImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transform: "translateZ(0)",
          }}
        />

        {/* OVERLAY */}
        <div className="absolute inset-0 bg-black/75 z-10" />

        {/* CONTENT */}
        <div className="relative z-20 min-h-screen flex flex-col justify-center items-center px-4 sm:px-6">
          <div className="flex flex-col justify-center items-center gap-6 sm:gap-8 p-6 sm:p-4 w-full md:w-[80%] lg:w-[70%] rounded-lg backdrop-blur-sm">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-designColor text-center">
              {service.title}
            </h1>

            <p className="text-sm sm:text-base md:text-lg text-center leading-relaxed text-white">
              {service.hero_description || service.description}
            </p>

            <BasicBreadcrumbs number={service.id} />
          </div>

          {showArrow && (
            <div className="absolute bottom-8 animate-bounce text-3xl text-white">
              <BsArrowDownSquareFill />
            </div>
          )}
        </div>
      </div>

      {/* STEPS */}
      {service.steps && service.steps.length > 0 && (
        <div className="flex flex-col gap-20 pt-[100px] px-4 md:px-10 lg:px-20">
          {service.steps.map((step, index) => (
            <div
              key={step.id}
              className={`flex flex-col lg:flex-row ${
                index % 2 === 0 ? "lg:flex-row-reverse" : ""
              } items-center gap-10`}
            >
              {/* IMAGE */}
              <div className="relative w-full lg:w-1/3 overflow-hidden rounded-lg">
                <img
                  src={
                    step.image_url ||
                    "https://via.placeholder.com/600x400"
                  }
                  alt={step.title}
                  loading="lazy"
                  className="w-full h-[300px] object-cover rounded-lg transform-gpu"
                />
              </div>

              {/* CONTENT */}
              <div
                className="lg:w-2/3 flex flex-col gap-5"
                style={{ textAlign: "center" }}
              >
                <span className="text-designColor font-bold">
                  {currentLang === "ar"
                    ? `الخطوة ${step.step_number}`
                    : `Step ${step.step_number}`}
                </span>

                <h2 className="text-3xl font-bold leading-tight">
                  {step.title}
                </h2>

                <p className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Contact />
    </div>
  );
}

export default ServicesDetails;